/*
 * Created by Ali Simsek on 5/14/20, 2:02 PM
 * Copyright (c) 2020 Carla Car Rental, Inc. All rights reserved.
 * Last modified 5/13/20, 6:32 PM
 */

import { any, arrayOf, bool, number, object, oneOf, oneOfType, shape, string } from 'prop-types'

const RentalPrice = shape({
  fullDeposit: number.isRequired,
  discountedDeposit: number,
  remainder: number.isRequired,
  currency: string.isRequired
})

const Supplier = shape({
  name: string.isRequired,
  logoUrl: string
})

const Station = shape({
  name: string.isRequired,
  wherePickup: string,
  address: string,
  phoneNumber: string,
  brokerLocationId: string,
  countryCode: string,
  utcOffsetInMinutes: number,
  city: string,
  dateTime: string,
  locationName: string
})

const PickupDropOffPair = shape({
  pickup: Station,
  dropOff: Station
})

const SearchArgs = shape({
  pickupDateTime: string.isRequired,
  dropOffDateTime: string.isRequired,
  pickupLocation: string.isRequired,
  dropOffLocation: string.isRequired,
  pickupLocationName: string,
  dropOffLocationName: string,
  age: oneOfType([number, string]),
  promoCode: string
})

const Car = shape({
  groupDescription: string.isRequired,
  transmissionDescription: string.isRequired,
  fuelDescription: string.isRequired,
  carType: oneOf(['Economy - Compact', 'Van', 'SUV', 'Luxury - Convertible', 'Intermediate - Fullsize']),
  seatCategory: string.isRequired,
  name: string.isRequired,
  classCode: string.isRequired,
  imageUrl: string,
  seatCount: string.isRequired
})

const CarPolicy = shape({
  title: string,
  description: string
})

const TripProtectionQuote = shape({
  broker: string.isRequired,
  id: string.isRequired,
  title: string.isRequired,
  subTitle: string.isRequired,
  price: number.isRequired,
  currency: string.isRequired
})

const PeaceOfMindQuote = shape({
  broker: string.isRequired,
  id: string.isRequired,
  title: string.isRequired,
  subTitle: string.isRequired,
  price: number.isRequired,
  currency: string.isRequired
})

const PriceInfoItem = shape({
  header: string.isRequired,
  description: string.isRequired
})

const Extras = shape({
  prioritized: bool.isRequired,
  insured: bool.isRequired,
  insuranceType: string,
  highlight: string,
  needsModification: bool.isRequired,
  shouldPrefetchForModification: bool.isRequired,
  score: number.isRequired,
  payableWithDebit: bool.isRequired,
  insuranceAvailable: bool,
  importantInfo: arrayOf(CarPolicy),
  alternativePaymentTypes: arrayOf(oneOf(['applePay', 'googlePay', 'alipay', 'giropay', 'ideal', 'uplift', 'affirm'])),
  tripProtectionQuote: TripProtectionQuote,
  peaceOfMindQuote: PeaceOfMindQuote
})

const InclusionInfo = shape({
  title: string.isRequired,
  inclusions: arrayOf(string)
})

const GenericPrice = shape({
  price: number.isRequired,
  currency: string.isRequired
})

const AncillaryProduct = shape({
  id: string.isRequired,
  broker: string.isRequired,
  policyCode: string,
  title: string.isRequired,
  subtitle: string.isRequired,
  price: GenericPrice.isRequired,
  preSelected: bool.isRequired,
  deselectedSubtitle: string,
  type: oneOf(['TripCancellation', 'PeaceOfMind', 'Insurance', 'RoadsideAssistance', 'SkipTheLine']).isRequired,
  priceText: string.isRequired,
  reference: string.isRequired,
  info: InclusionInfo
})

const CarRentalProduct = shape({
  productHash: string.isRequired,
  car: Car.isRequired,
  price: RentalPrice.isRequired,
  priceInfoExtras: arrayOf(PriceInfoItem),
  stationPair: PickupDropOffPair,
  supplier: Supplier,
  inclusions: arrayOf(string),
  extras: Extras.isRequired,
  dependencies: object
})

const CarSearchResponse = shape({
  strictCancellationPolicy: string.isRequired,
  flexibleCancellationPolicy: string.isRequired,
  termsPartOne: string.isRequired, // termsAndConditions
  termsPartTwo: string.isRequired, // refundPolicy
  searchId: string.isRequired,
  searchArgs: SearchArgs.isRequired,
  //paymentCardInfo
  cars: arrayOf(CarRentalProduct).isRequired
})

const PriceInfoDetail = shape({
  title: string,
  subtitle: string,
  items: arrayOf(PriceInfoItem)
})

const ExtendedPrice = shape({
  rentalPrice: RentalPrice.isRequired,
  extraRentalPrice: arrayOf(PriceInfoItem),
  ancillaryProducts: arrayOf(AncillaryProduct),
  selectedAncillaries: any.isRequired
})

const SelectItem = shape({
  label: oneOfType([string, number]),
  value: any
})

const TimeObject = shape({
  hour: number,
  min: number
})

const CompleteCarRentalReservationInfo = shape({
  confirmationNo: string.isRequired,
  reservationId: string.isRequired,
  isEligibleForDateChange: bool.isRequired,
  rentalProduct: {
    name: string.isRequired,
    imageUrl: string.isRequired,
    supplierUrl: string.isRequired,
    totalPrice: number.isRequired,
    currency: string.isRequired,
    productHash: string.isRequired,
    classCode: string.isRequired,
    typeDescription: string,
    remainder: number.isRequired
  },
  userInfo: {
    email: string.isRequired,
    firstName: string.isRequired,
    lastName: string.isRequired,
    phoneNumber: string
  },
  dtl: {
    pickup: {
      dateTime: string.isRequired,
      locationName: string.isRequired,
      address: string
    },
    dropOff: {
      dateTime: string.isRequired,
      locationName: string.isRequired,
      address: string
    }
  },
  wherePickup: string,
  broker: string,
  paymentCardRequirements: {
    debitCard: [
      {
        type: {
          value: string
        },
        name: string,
        details: string
      }
    ],
    creditCard: [
      {
        type: {
          value: string
        },
        name: string,
        details: string
      }
    ]
  },
  isEligibleForUpgrade: bool,
  iaInsuranceSelected: bool,
  isEligibleForHotelDeals: bool,
  isEligibleForPostCancellation: bool,
  isEligibleForIgnorePopUp: bool,
  status: string,
  isEligibleForModification: bool,
  roadsideAssistancePurchased: bool,
  shortInfoText: string,
  servicingSupplier: {
    name: string,
    logoUrl: string
  },
  conversionValue: {
    transactionId: string,
    revenue: {
      price: number,
      currency: string,
      items: [
        {
          itemId: string,
          itemName: string,
          price: {
            price: number,
            currency: string
          }
        }
      ]
    }
  }
})

export const CarlinPropTypes = {
  CarSearchResponse,
  SearchArgs,
  CarRentalProduct,
  Car,
  RentalPrice,
  Supplier,
  Station,
  PickupDropOffPair,
  CarPolicy,
  TripProtectionQuote,
  PriceInfoItem,
  Extras,
  ExtendedPrice,
  PriceInfoDetail,
  SelectItem,
  TimeObject,
  PeaceOfMindQuote,
  AncillaryProduct,
  CompleteCarRentalReservationInfo
}
